import { FC, SyntheticEvent, useMemo } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { isNullish, values } from 'remeda';
import useSWR from 'swr';

import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { DateTimePicker } from '@mui/x-date-pickers';

import { ITournamentForm, validationSchema } from './form';
import { TournamentRewardsFormControl } from './tournamentRewardsFormControl/TournamentRewardsFormControl';
import { mapFormToDto } from './utils';

import css from './TournamentFormDialog.module.css';

import { tournamentGroupService } from 'v2/api/tournamentGroupService';
import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';
import { ProviderName, SELECT_NONE_OPTION } from 'v2/constants';
import { SelectOption } from 'v2/types';
import { ImagesBreakPoints } from 'v2/types/ImagesBreakPoints';
import { TournamentForm } from 'v2/types/TournamentModel';

interface TournamentFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: ITournamentForm;
  isSubmitting: boolean;
  onSubmit: (values: TournamentForm, resetForm: VoidFunction) => void;
}

export const TournamentFormDialog: FC<TournamentFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const { data: groups } = useSWR(isOpen && 'getTournamentGroups', tournamentGroupService.getTournamentGroups);

  const formik = useFormik<ITournamentForm>({
    initialValues,
    onSubmit: (values, { resetForm }) => {
      onSubmit(mapFormToDto(values), resetForm);
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
  });

  const groupsOptions: SelectOption[] = useMemo(() => {
    if (isNullish(groups)) return [];

    return [SELECT_NONE_OPTION, ...groups.map((g) => ({ id: g.id, value: g.id, label: g.name }))];
  }, [groups]);

  const handleSelectChange = (field: keyof ITournamentForm) => (v: unknown) => {
    formik.setFieldValue(field, v);
  };

  const handleImagesSelectChange = (field: keyof ImagesBreakPoints) => (v: unknown) => {
    formik.setFieldValue(`images.${field}`, v);
  };

  const handleChangeProviders = (values: ProviderName[]) => {
    formik.setFieldValue('providerNames', values);
  };

  const handleFirstChange = (_: SyntheticEvent, checked: boolean) => {
    formik.setFieldValue('isFirstInGroup', checked);

    if (checked) {
      formik.setFieldValue('isLastInGroup', false);
    }
  };

  const handleLastChange = (_: SyntheticEvent, checked: boolean) => {
    formik.setFieldValue('isLastInGroup', checked);

    if (checked) {
      formik.setFieldValue('isFirstInGroup', false);
    }
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="tournamentForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="name"
            value={formik.values.name}
            error={!!formik.errors.name}
            helperText={formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            required
          />

          <TextField
            name="description"
            value={formik.values.description}
            error={!!formik.errors.description}
            helperText={formik.errors.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Description"
            rows={2}
            multiline
            required
          />

          <div className="form-group">
            <TextField
              name="balance"
              value={formik.values.balance}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Balance"
              type="number"
              required
              fullWidth
            />

            <TextField
              name="prizeFund"
              value={formik.values.prizeFund}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Prize Fund"
              type="number"
              required
              fullWidth
            />
          </div>

          <CheckBoxAutocomplete
            label="Providers"
            value={formik.values.providerNames}
            options={values(ProviderName)}
            onChange={handleChangeProviders}
            required
          />

          <div className={clsx('form-group', css.tournamentFormGroupDates)}>
            <DateTimePicker
              value={formik.values.startDate}
              onChange={handleSelectChange('startDate')}
              label="Start Date *"
              format="DD.MM.YYYY HH:mm"
              minDate={dayjs()}
            />

            <DateTimePicker
              value={formik.values.endDate}
              onChange={handleSelectChange('endDate')}
              label="End Date *"
              format="DD.MM.YYYY HH:mm"
              minDate={dayjs(formik.values.startDate)}
            />
          </div>

          <div className="form-group">
            <Typography variant="subtitle1">Images</Typography>
          </div>

          <div className="form-group">
            <FileSelector
              value={formik.values.images.image390Id}
              error={formik.errors.images?.image390Id}
              label="390px"
              onSelect={handleImagesSelectChange('image390Id')}
              required
            />

            <FileSelector
              value={formik.values.images.image768Id ?? ''}
              error={formik.errors.images?.image768Id}
              label="768px"
              onSelect={handleImagesSelectChange('image768Id')}
            />

            <FileSelector
              value={formik.values.images.image1024Id}
              error={formik.errors.images?.image1024Id}
              label="1024px"
              onSelect={handleImagesSelectChange('image1024Id')}
              required
            />

            <FileSelector
              value={formik.values.images.image1300Id ?? ''}
              error={formik.errors.images?.image1300Id}
              label="1300px"
              onSelect={handleImagesSelectChange('image1300Id')}
            />
          </div>

          <Select
            value={formik.values.groupId}
            options={groupsOptions}
            onChange={handleSelectChange('groupId')}
            label="Group"
          />

          {formik.values.groupId && (
            <div className={clsx('form-group', css.tournamentFormGroupCheckboxes)}>
              <Switch
                name="isFirstInGroup"
                checked={formik.values.isFirstInGroup}
                onChange={handleFirstChange}
                label="Is First In Group"
              />

              <Switch
                name="isLastInGroup"
                checked={formik.values.isLastInGroup}
                onChange={handleLastChange}
                label="Is Last In Group"
              />
            </div>
          )}

          <TournamentRewardsFormControl formik={formik} />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="tournamentForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
